import gql from 'graphql-tag'
import client from '@stores/apollo'
import { ICurrentUser } from './types'
import { EmployeeDetailsFragment, FloorPlanFragment } from '@api/fragments'
import handleFetchResult from '@utils/handleFetchResult'

interface IPayload {
  viewer: ICurrentUser
}

const defaultUserContext = {
  units: 'sqft',
}

const mutateHandler = handleFetchResult<IPayload, ICurrentUser>({
  handler: (data) => ({
    ...data.viewer,
    context: {
      ...defaultUserContext,
      ...JSON.parse(data.viewer.context as unknown as string),
    },
  }),
  name: 'Current User',
})

export default function current(fromDate?: string): Promise<ICurrentUser> {
  return client.gql
    .query({ query: fromDate ? currentUserQueryWithBookings : currentUserQuery })
    .then(mutateHandler)
}

export const currentUserQuery = gql`
  query ($assignmentPlanId: ID) {
    viewer {
      context
      id
      isSuperUser
      isClassic
      isAuthAppEnabled
      settings {
        healthNoticeEnabled
        defaultFloor
        favoriteFloors
      }
      bookingManagerSetting: bookingsManagerSetting {
        manage_all
        managed_departments {
          employeeConnection {
            edges {
              node {
                name
                id
                email
              }
            }
          }
        }
        managed_employees {
          name
          id
          email
        }
      }
      employee {
        ...EmployeeDetailsFragment
        preferredLocations {
          id
        }
        locationAdmins {
          floorPlan {
            ...FloorPlanFragment
          }
        }
        departmentAdmins {
          department {
            id
            name
          }
        }
      }
      company {
        id
        uuid
        name
        settings {
          employeeImportNotificationEnabled
          hotelingDeskDeptEnforcementEnabled
          enforceAuthAppEnabled
          enableHealthScreening
          bookingCheckInCancelEnabled
          bookingCancellationEnabled
          qrCodeEnabled
          healthScreeningConfirmButtonText
          healthScreeningMessage
          hoursBeforeBookingCheckin
          hoursHealthMessageSentBeforeBooking
        }
        userVoiceUrl
      }
    }
  }
  ${EmployeeDetailsFragment}
  ${FloorPlanFragment}
`

export const currentUserQueryWithBookings = gql`
  query ($assignmentPlanId: ID, $fromDate: String) {
    viewer {
      context
      id
      isSuperUser
      isAuthAppEnabled
      settings {
        healthNoticeEnabled
        defaultFloor
        favoriteFloors
      }
      employee {
        bookings(fromDate: $fromDate) {
          id
          role
          name
          startDate
          endDate
          checkedIn
          passedHealthCheck
          bookingSchedule {
            id
            recurrencePattern
            effectiveEndDate
            effectiveStartDate
            pattern {
              dayOfMonth
              daysOfWeek
              interval
              type
            }
            range {
              endDate
              numberOfOccurrences
              startDate
              type
            }
          }
          healthCheck {
            disabled
            locationHeathCheckGroup {
              id
            }
          }
          space {
            id
            externalId
            code
            floor {
              id
              name
              code
              enforceQrCode
              printableQrCodeUsageTypes
              building {
                id
                name
                city
                timezone
                address
                code
              }
            }
            usageType {
              displayName
              name
            }
          }
        }
        ...EmployeeDetailsFragment
        locationAdmins {
          floorPlan {
            ...FloorPlanFragment
          }
        }
        departmentAdmins {
          department {
            id
          }
        }
      }
      company {
        id
        uuid
        name
        settings {
          employeeImportNotificationEnabled
          hotelingDeskDeptEnforcementEnabled
          enforceAuthAppEnabled
          enableHealthScreening
          bookingCheckInCancelEnabled
          qrCodeEnabled
        }
      }
    }
  }
  ${EmployeeDetailsFragment}
  ${FloorPlanFragment}
`
